import type { HolidayRange } from '@/utils/holidayRanges'
import { formatDayAndMonth } from '@/utils/dateFormat'

/**
 * Turning a stored holiday into something a customer would say.
 *
 * A holiday is stored as [start, end) where `end` is the RESTART date and is
 * not itself paused. That is right for the data and wrong for a person:
 * nobody describes a break as "the 1st to the 11th" when the last day off is
 * the 10th. Everything customer-facing goes through here so the conversion is
 * written once rather than at each call site.
 *
 * Separate from the components that use it so React fast refresh keeps
 * working: a file exporting both a component and helpers loses it.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** The last day actually skipped, which is the day before the restart date. */
export const lastSkippedDay = (range: HolidayRange): Date =>
  new Date(range.end.getTime() - DAY_MS)

/** A skip covering one delivery only. */
export const isOneOff = (range: HolidayRange): boolean =>
  lastSkippedDay(range).getTime() <= range.start.getTime()

/**
 * A YYYY-MM-DD string as a LOCAL date.
 *
 * `new Date('2026-09-10')` is parsed as UTC, which lands on the previous day
 * for anyone behind it and makes date arithmetic silently wrong. The same
 * trap is documented in utils/dateFormat.
 */
export const parseLocalDate = (value: string): Date => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

/** The day after a given YYYY-MM-DD, which is what a restart date is. */
export const dayAfter = (value: string): Date => {
  const d = parseLocalDate(value)
  d.setDate(d.getDate() + 1)
  return d
}

/** "24th December" for a single day, "1st to 10th September" for a range. */
export const describeRange = (range: HolidayRange): string => {
  const last = lastSkippedDay(range)

  if (isOneOff(range)) {
    return formatDayAndMonth(range.start)
  }

  // Same month reads better without repeating it: "1st to 10th September".
  if (last.getMonth() === range.start.getMonth() && last.getFullYear() === range.start.getFullYear()) {
    const startDay = formatDayAndMonth(range.start).split(' ')[0]
    return `${startDay} to ${formatDayAndMonth(last)}`
  }

  return `${formatDayAndMonth(range.start)} to ${formatDayAndMonth(last)}`
}
